// Tiny typed fetch wrapper that routes every request through the API URL
// baked into the Vite build, carries the session cookie, and normalises
// the error shape returned by the Hono routes.
//
// Used by the wave-2 UI pages so each one doesn't redeclare `fetch(base...
// {credentials:'include'})` boilerplate.

import type { CreateTournamentBody, TournamentSummary } from '~/api/admin';
import { env } from './env';

const base = env.VITE_API_URL.replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    const msg =
      (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : null) ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

// ── Tournament admin wrappers ────────────────────────────────────────────────

export async function adminListUpcomingTournaments(): Promise<TournamentSummary[]> {
  const res = await api<{ items: TournamentSummary[] }>('/tournaments/upcoming');
  return res.items;
}

export async function adminListPastTournaments(): Promise<TournamentSummary[]> {
  const res = await api<{ items: TournamentSummary[] }>('/tournaments/past');
  return res.items;
}

export async function adminCreateTournament(body: CreateTournamentBody): Promise<{ id: string }> {
  return api<{ id: string }>('/tournaments', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function adminCancelTournament(id: string): Promise<void> {
  await api<{ ok: true }>(`/tournaments/${id}`, { method: 'DELETE' });
}

// Permanently delete a cancelled or finished tournament (and its matches).
export async function adminDeleteTournament(id: string): Promise<void> {
  await api<{ ok: true }>(`/admin/tournaments/${id}`, { method: 'DELETE' });
}
